import { useRef, useEffect, useLayoutEffect, ReactNode } from "react";
import { useTooltipContext } from "./tooltip-provider";
import { TOOLTIPS } from "./tooltip-registry";
import { Tooltip } from "./tooltip";

interface TooltipTargetProps {
  tooltipId: string;
  children: ReactNode;
}

/**
 * Wrapper component that marks an element as a tooltip target.
 * When the tooltip with the given ID is active, it will be rendered
 * pointing to this element.
 *
 * Note: Uses display:contents wrapper but registers the actual first child
 * element for positioning, since display:contents elements return 0,0
 * from getBoundingClientRect().
 */
export function TooltipTarget({ tooltipId, children }: TooltipTargetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    activeTooltipId,
    registerTarget,
    unregisterTarget,
    getTargetElement,
    dismissTooltip,
  } = useTooltipContext();

  // Register the actual first child element (not the display:contents wrapper)
  // since getBoundingClientRect() returns 0,0 for display:contents elements
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container) {
      // Find the first actual element child (skip text nodes)
      const firstChild = container.firstElementChild as HTMLElement | null;
      registerTarget(tooltipId, firstChild);
    }
    return () => unregisterTarget(tooltipId);
  }, [tooltipId, registerTarget, unregisterTarget]);

  const isActive = activeTooltipId === tooltipId;
  const tooltipDef = TOOLTIPS[tooltipId];
  const targetElement = getTargetElement(tooltipId);

  return (
    <div ref={containerRef} style={{ display: "contents" }}>
      {children}
      {isActive && tooltipDef && (
        <Tooltip
          content={tooltipDef.content}
          position={tooltipDef.position}
          targetElement={targetElement}
          onDismiss={() => dismissTooltip(tooltipId)}
          isVisible={isActive}
        />
      )}
    </div>
  );
}
